import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Logo } from '../common/Logo';
import { trackEvent } from '../../utils/analytics';

interface NavbarProps {
  showGetStarted?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ showGetStarted = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-3' : 'py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`flex justify-between items-center px-6 py-3 rounded-2xl border transition-all duration-300 ${
            isScrolled
              ? 'bg-white/80 border-cyan-500/10'
              : 'bg-transparent border-transparent'
          }`}
          style={{
            backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
            WebkitBackdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
            boxShadow: isScrolled ? '0 4px 24px rgba(0, 0, 0, 0.08)' : 'none',
          }}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <Logo variant="app-icon" size={36} isDarkMode={false} />
            <span className="text-lg font-semibold tracking-tight text-gray-800">
              vidya.ai
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Features
            </a>
            <a
              href="#demo"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Demo
            </a>
            <a
              href="#testimonials"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Testimonials
            </a>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="hidden md:block text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign In
            </Link>

            <AnimatePresence>
              {showGetStarted && (
                <Link to="/signup" onClick={() => trackEvent('cta_click', { cta_text: 'get_started', cta_location: 'navbar' })}>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex px-5 py-2 rounded-full text-sm font-medium text-white transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
                    }}
                  >
                    Get Started
                  </motion.span>
                </Link>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-2 p-4 rounded-2xl border border-cyan-500/10"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900">
                Features
              </a>
              <a href="#demo" className="text-gray-600 hover:text-gray-900">
                Demo
              </a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900">
                Testimonials
              </a>
              <hr className="border-gray-200" />
              <Link to="/login" className="text-gray-600 hover:text-gray-900">
                Sign In
              </Link>
              <Link
                to="/signup"
                onClick={() => trackEvent('cta_click', { cta_text: 'get_started', cta_location: 'navbar_mobile' })}
                className="text-center py-2 rounded-full text-white font-medium"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                }}
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
